
import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Camera, X, Check, AlertCircle, Loader } from "lucide-react";
import {
  validateQRCode,
  ValidateandAcumlateQRCodes,
  giftRedeemQRCode,
} from "../../redux/actions/salesmanAction";
import { FaArrowLeft } from "react-icons/fa";
import { DashboardLayout } from "../../Pages/dashboard/DashboardLayout";
import AccumulationResultModal from "../../Pages/SalesMan/AccumulationResultModal";
import RedeemGiftModal from "../../Pages/SalesMan/RedeemGiftModal";

const QRScanner = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);
  const location = useLocation();
  const token = userInfo?.access_token;

  // Get customer data from navigation state
  const customerData = location.state?.customerData;
  const customerId = customerData?.customer_id;

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const animationIdRef = useRef(null);

  const [scannedQRs, setScannedQRs] = useState([]);
  const [cameraError, setCameraError] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [detectedQR, setDetectedQR] = useState(null);
  const [lastDetected, setLastDetected] = useState(null);
  const [validatingQRId, setValidatingQRId] = useState(null);
  const [scannerMessage, setScannerMessage] = useState(null);
  const [isAccumulating, setIsAccumulating] = useState(false);
  const [accumulationResult, setAccumulationResult] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [redeemingGiftId, setRedeemingGiftId] = useState(null);
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [isSubmittingRedeem, setIsSubmittingRedeem] = useState(false);

  // Load jsQR library
  useEffect(() => {
    if (!window.jsQR) {
      const script = document.createElement("script");
      script.src = "https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.js";
      script.async = true;
      document.head.appendChild(script);
    }
  }, []);

  // Auto-start scanner when component loads with customer data
  useEffect(() => {
    if (customerData && customerId && userInfo && token && !isScanning) {
      // Small delay to ensure jsQR is loaded
      const timer = setTimeout(() => {
        startScanner();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [customerData, customerId, userInfo, token]);

  // Start real-time scanner
  const startScanner = async () => {
    try {
      setCameraError(null);
      setIsScanning(true);

      // Check if getUserMedia is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        // Check if we're on HTTP (not HTTPS or localhost)
        const isHTTP = window.location.protocol === 'http:';
        const isLocalhost = window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1';
        
        if (isHTTP && !isLocalhost) {
          setCameraError(
            "Camera access requires HTTPS. Please access the app via HTTPS URL. " +
            "The app is now running on HTTPS - refresh the page and try again."
          );
      } else {
        setCameraError(
          "Camera access not available. Your browser may not support camera access, " +
          "or you need to grant camera permissions."
        );
      }
        setIsScanning(false);
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
        audio: false,
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      scanQRLoop();
    } catch (err) {
      let errorMessage = err.message;
      
      // Provide helpful error messages
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage = "Camera permission denied. Please allow camera access in your browser settings and try again.";
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMessage = "No camera found. Please connect a camera.";
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        errorMessage = "Camera is already in use by another application. Please close other apps using the camera.";
      } else if (err.message.includes('getUserMedia')) {
        const isHTTP = window.location.protocol === 'http:';
        const isLocalhost = window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1';
        if (isHTTP && !isLocalhost) {
          errorMessage = "Camera access requires HTTPS when accessing via IP address. Please use HTTPS URL.";
        }
      }
      
      setCameraError(`Camera error: ${errorMessage}`);
      setIsScanning(false);
    }
  };

  // Stop scanner
  const stopScanner = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (animationIdRef.current) {
      cancelAnimationFrame(animationIdRef.current);
    }
    setIsScanning(false);
    setDetectedQR(null);
  };

  // QR Scanning loop with jsQR
  const scanQRLoop = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const video = videoRef.current;

    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      if (window.jsQR) {
        const code = window.jsQR(
          imageData.data,
          imageData.width,
          imageData.height
        );

        if (code && code.data) {
          const qrValue = code.data.trim();

          const alreadyScanned = scannedQRs.some((qr) => qr.code === qrValue);

          if (qrValue !== lastDetected && !alreadyScanned) {
            setDetectedQR(qrValue);
            setLastDetected(qrValue);

            if (navigator.vibrate) navigator.vibrate([100, 50, 100]);

            addQRCode(qrValue);

            setTimeout(() => {
              setDetectedQR(null);
              setLastDetected(null);
            }, 5000);
          } else if (alreadyScanned) {
            setScannerMessage({
              type: "duplicate",
              text: " Already scanned! Scan next QR code",
            });
            setTimeout(() => setScannerMessage(null), 2000);
          }
        } else {
          setDetectedQR(null);
        }
      }
    }

    animationIdRef.current = requestAnimationFrame(scanQRLoop);
  };

  // Add QR code and dispatch validation
  const addQRCode = (code) => {
    const qrId = code.trim();

    if (!qrId || qrId.length < 3) return false;

    if (!userInfo || !token) {
      alert("❌ Please login first before scanning QR codes");
      return false;
    }

    const exists = scannedQRs.some((qr) => qr.code === qrId);
    if (exists) {
      console.log("Duplicate QR detected:", qrId);
      return false;
    }

    const newQR = {
      code: qrId,
      timestamp: new Date().toLocaleTimeString(),
      id: Date.now(),
      validationStatus: null,
      validationError: null,
    };

    setScannedQRs((prev) => {
      const isDuplicate = prev.some((qr) => qr.code === qrId);
      if (isDuplicate) {
        console.log("Duplicate caught in setState:", qrId);
        return prev;
      }
      return [...prev, newQR];
    });

    setValidatingQRId(qrId);

    const validationPromise = dispatch(validateQRCode({ qr_id: qrId }, token));

    if (validationPromise && typeof validationPromise.then === "function") {
      validationPromise
        .then((res) => {
          if (res.type === "VALIDATE_QR_SUCCESS") {
            setScannedQRs((prev) =>
              prev.map((qr) =>
                qr.code === qrId ? { ...qr, validationStatus: "valid" } : qr
              )
            );
            setScannerMessage({
              type: "valid",
              text: " Valid! Scan next QR code",
            });
            setTimeout(() => setScannerMessage(null), 10000);
          } else {
            const errorMsg =
              res.payload?.detailed_message || "Validation failed";
            setScannedQRs((prev) =>
              prev.map((qr) =>
                qr.code === qrId
                  ? {
                      ...qr,
                      validationStatus: "error",
                      validationError: errorMsg,
                    }
                  : qr
              )
            );
            setScannerMessage({
              type: "error",
              text: `✗ ${errorMsg}`,
            });
            setTimeout(() => setScannerMessage(null), 10000);
          }
          setValidatingQRId(null);
        })
        .catch((err) => {
          console.error("Validation error:", err);
          const errorMessage =
            err.response?.data?.message || err.message || "Validation failed";

          setScannedQRs((prev) =>
            prev.map((qr) =>
              qr.code === qrId
                ? {
                    ...qr,
                    validationStatus: "error",
                    validationError: errorMessage,
                  }
                : qr
            )
          );
          setScannerMessage({
            type: "error",
            text: `✗ ${errorMessage}`,
          });
          setTimeout(() => setScannerMessage(null), 10000);
          setValidatingQRId(null);
        });
    }

    return true;
  };

  // Remove QR
  const removeQR = (id) => {
    setScannedQRs((prev) => prev.filter((qr) => qr.id !== id));
  };

  // Clear all
  const clearAll = () => {
    if (window.confirm("Clear all QR codes?")) {
      setScannedQRs([]);
    }
  };

  // Handle Accumulate
  const handleAccumulate = async () => {
    if (scannedQRs.length === 0) {
      alert("No QR codes to accumulate");
      return;
    }

    if (!customerId) {
      alert(
        "Customer information not found. Please go back and select a customer."
      );
      return;
    }

    setIsAccumulating(true);

    try {
      const qrIds = scannedQRs.map((qr) => qr.code);
      const payload = {
        qr_ids: qrIds,
        customer_id: customerId,
        is_accumulated: true,
      };

      console.log("Accumulate Payload:", payload);

      const result = await dispatch(ValidateandAcumlateQRCodes(payload, token));

      if (
        result?.type === "REDEEM_QR_SUCCESS" ||
        result?.payload?.status === 200
      ) {
        setAccumulationResult(result.payload);
        setShowResultModal(true);
        setScannedQRs([]);
      } else {
        const errorMsg = result?.payload?.message || "Accumulation failed";
        alert(`✗ Error: ${errorMsg}`);
      }
    } catch (error) {
      console.error("Accumulation error:", error);
      alert(`✗ Failed to accumulate: ${error.message}`);
    } finally {
      setIsAccumulating(false);
    }
  };

  // Handle Redeem Gift
  const handleRedeemGift = (giftId) => {
    setRedeemingGiftId(giftId);
    setShowRedeemModal(true);
  };

  // Submit Redeem
  // const handleSubmitRedeem = async (data) => {
  //   const { signature, selfie } = data;

  //   if (!signature) {
  //     alert("Please provide a signature");
  //     return;
  //   }
  //   if (!selfie) {
  //     alert("Please provide a selfie/photo");
  //     return;
  //   }

  //   const confirmed = window.confirm(
  //     "Confirm gift redemption with signature and selfie?"
  //   );
  //   if (!confirmed) return;

  //   setIsSubmittingRedeem(true);

  //   try {
  //     const qrIds = accumulationResult?.qr_ids || [];

  //     let lat = "0";
  //     let long = "0";

  //     if (navigator.geolocation) {
  //       await new Promise((resolve) => {
  //         navigator.geolocation.getCurrentPosition(
  //           (position) => {
  //             lat = position.coords.latitude.toString();
  //             long = position.coords.longitude.toString();
  //             resolve();
  //           },
  //           (error) => {
  //             console.log("Geolocation error:", error);
  //             resolve();
  //           }
  //         );
  //       });
  //     }

  //     const address =
  //       customerData?.address || customerData?.firm_name || "Not provided";
  //     const city = customerData?.city || "Not provided";
  //     const state = customerData?.state || "Not provided";
  //     const pincode = customerData?.pincode || "Not provided";

  //     const payload = {
  //       qr_ids: qrIds,
  //       customer_id: customerId,
  //       gift_id: redeemingGiftId,
  //       customer_sign: signature,
  //       customer_selfie: selfie,
  //       address: address,
  //       city: city,
  //       state: state,
  //       pincode: pincode,
  //       lat: lat,
  //       long: long,
  //     };

  //     console.log("Redeem Payload:", {
  //       ...payload,
  //       customer_sign: payload.customer_sign.substring(0, 50) + "...",
  //       customer_selfie: payload.customer_selfie.substring(0, 50) + "...",
  //     });

  //     const result = await dispatch(giftRedeemQRCode(payload, token));

  //     if (
  //       result?.type === "GIFT_REDEEM_QR_SUCCESS" ||
  //       result?.payload?.status === 200
  //     ) {
  //       // alert("✓ Gift redeemed successfully!");
  //       setShowRedeemModal(false);
  //       setRedeemingGiftId(null);
  //       setShowResultModal(false);
  //       setScannedQRs([]);
  //       navigate("/dashboard/salesmancustomerlist");
  //     } else {
  //       const errorMsg = result?.payload?.message || "Gift redemption failed";
  //       alert(`✗ Error: ${errorMsg}`);
  //     }
  //   } catch (error) {
  //     console.error("Redemption error:", error);
  //     alert(`✗ Failed to redeem gift: ${error.message}`);
  //   } finally {
  //     setIsSubmittingRedeem(false);
  //   }
  // };

  // Replace your handleSubmitRedeem function with this:
// Replace your handleSubmitRedeem function with this:
const handleSubmitRedeem = async (data) => {
  return new Promise((resolve, reject) => {
    const { signature, selfie } = data;

    if (!signature) {
      alert("Please provide a signature");
      reject(new Error("No signature provided"));
      return;
    }
    if (!selfie) {
      alert("Please provide a selfie/photo");
      reject(new Error("No selfie provided"));
      return;
    }

    const confirmed = window.confirm(
      "Confirm gift redemption with signature and selfie?"
    );
    if (!confirmed) {
      reject(new Error("User cancelled redemption"));
      return;
    }

    setIsSubmittingRedeem(true);

    (async () => {
      try {
        const qrIds = accumulationResult?.qr_ids || [];

        let lat = "0";
        let long = "0";

        if (navigator.geolocation) {
          await new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                lat = position.coords.latitude.toString();
                long = position.coords.longitude.toString();
                resolve();
              },
              (error) => {
                console.log("Geolocation error:", error);
                resolve();
              }
            );
          });
        }

        const address =
          customerData?.address || customerData?.firm_name || "Not provided";
        const city = customerData?.city || "Not provided";
        const state = customerData?.state || "Not provided";
        const pincode = customerData?.pincode || "Not provided";

        const payload = {
          qr_ids: qrIds,
          customer_id: customerId,
          gift_id: redeemingGiftId,
          customer_sign: signature,
          customer_selfie: selfie,
          address: address,
          city: city,
          state: state,
          pincode: pincode,
          lat: lat,
          long: long,
        };

        console.log("Redeem Payload:", {
          ...payload,
          customer_sign: payload.customer_sign.substring(0, 50) + "...",
          customer_selfie: payload.customer_selfie.substring(0, 50) + "...",
        });

        const result = await dispatch(giftRedeemQRCode(payload, token));

        if (
          result?.type === "GIFT_REDEEM_QR_SUCCESS" ||
          result?.payload?.status === 200
        ) {
          // Close modal and reset state
          setShowRedeemModal(false);
          setRedeemingGiftId(null);
          setShowResultModal(false);
          setScannedQRs([]);
          
          // Resolve promise with full payload to show in success screen
          resolve({ 
            success: true,
            message: result?.payload?.message || "Gift redeemed successfully!",
            data: result?.payload?.data
          });
          
          // Then navigate after modal shows success message
          setTimeout(() => {
            navigate("/dashboard");
          }, 2500);
        } else {
          const errorMsg = result?.payload?.message || "Gift redemption failed";
          console.error(`Error: ${errorMsg}`);
          reject(new Error(errorMsg));
        }
      } catch (error) {
        console.error("Redemption error:", error);
        reject(new Error(`Failed to redeem gift: ${error.message}`));
      } finally {
        setIsSubmittingRedeem(false);
      }
    })();
  });
};
  return (
    <DashboardLayout>
      <div className="min-h-screen bg-black p-0">
        {/* Scanner View */}
        {isScanning ? (
          <div className="fixed inset-0 bg-black flex flex-col">
            <div className="flex-1 relative overflow-hidden">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                className="bg-black"
              />
              <canvas ref={canvasRef} className="hidden" />

              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative w-80 h-80">
                  <div
                    className={`absolute inset-0 border-4 rounded-2xl transition-all ${
                      detectedQR
                        ? "border-green-400 shadow-lg shadow-green-500"
                        : "border-green-300"
                    }`}
                  >
                    <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-green-400"></div>
                    <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-green-400"></div>
                    <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-green-400"></div>
                    <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-green-400"></div>
                  </div>

                  <div
                    className="absolute left-0 right-0 h-1 bg-gradient-to-b from-green-400 to-transparent animate-pulse"
                    style={{ top: "50%", animation: "scan 2s linear infinite" }}
                  ></div>
                </div>
              </div>

              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 left-0 right-0 h-24 bg-black bg-opacity-70"></div>
                <div className="absolute bottom-0 left-0 right-0 h-32 bg-black bg-opacity-70"></div>
                <div className="absolute top-24 left-0 w-16 h-32 bg-black bg-opacity-70"></div>
                <div className="absolute top-24 right-0 w-16 h-32 bg-black bg-opacity-70"></div>
              </div>

              {/* Scanner Message Banner - More Prominent */}
              <div className="absolute top-4 left-0 right-0 z-10 px-4">
                <div
                  className={`mx-auto max-w-md rounded-lg px-4 py-3 shadow-2xl transition-all ${
                    scannerMessage
                      ? scannerMessage.type === "valid"
                        ? "bg-green-500 border-2 border-green-300"
                        : scannerMessage.type === "duplicate"
                        ? "bg-yellow-500 border-2 border-yellow-300"
                        : "bg-red-500 border-2 border-red-300"
                      : detectedQR
                      ? "bg-blue-500 border-2 border-blue-300"
                      : "bg-black bg-opacity-60 border-2 border-gray-400"
                  }`}
                >
                  <p
                    className={`text-center font-bold text-base sm:text-lg md:text-xl transition-all ${
                      scannerMessage
                        ? scannerMessage.type === "valid"
                          ? "text-white"
                          : scannerMessage.type === "duplicate"
                          ? "text-white"
                          : "text-white"
                        : "text-white"
                    }`}
                  >
                    {scannerMessage
                      ? scannerMessage.text
                      : detectedQR
                      ? "✓ QR Code Detected!"
                      : "Scan QR Code"}
                  </p>
                </div>
              </div>

              <div className="absolute bottom-20 left-0 right-0 text-center">
                <p className="text-white text-xs opacity-75">
                  Position QR code inside the frame
                </p>
              </div>
            </div>

            <div className="bg-black bg-opacity-80 p-4 flex justify-center gap-4">
              <button
                onClick={stopScanner}
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-full font-semibold transition flex items-center gap-2"
              >
                <X className="w-5 h-5" />
                Close Scanner
              </button>
            </div>
          </div>
        ) : (
          /* Main View */
          <div className="bg-gradient-to-br from-blue-50 to-indigo-100 min-h-screen p-4">
            <div className="max-w-2xl mx-auto">
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => navigate(-1)}
                  className="hover:text-gray-800"
                >
                  <FaArrowLeft size={20} />
                </button>
                <h2 className="text-xl sm:text-2xl font-bold text-amber-600 my-4 ">
                  QR Scanner
                </h2>
              </div>

              <div className=" bg-white rounded-3xl shadow-2xl overflow-hidden mb-8">
                <div className=" bg-amber-500 p-8">
                  <div className="text-center mb-6">
                    <div className="inline-block bg-blue-500 bg-opacity-30 rounded-full p-2 mb-4">
                      <Camera className="w-6 h-6 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-2">
                      Scan QR Code
                    </h2>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    <button
                      onClick={startScanner}
                      disabled={!userInfo || !token}
                      className={`${
                        !userInfo || !token
                          ? "opacity-50 cursor-not-allowed "
                          : "bg-white"
                      } text-amber-600 font-bold py-4 px-4 rounded-xl transition transform hover:scale-105 flex flex-col items-center gap-2 shadow-lg disabled:hover:scale-100`}
                    >
                      <Camera className="w-8 h-8" />
                      <span className="text-sm">Click Here to Scan</span>
                    </button>
                  </div>
                </div>
              </div>

              {!userInfo || !token ? (
                <div className="bg-orange-100 border-l-4 border-orange-500 text-orange-700 p-4 rounded mb-6 flex gap-3">
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold">Authentication Required</p>
                    <p className="text-sm mt-1">
                      Please login first to scan QR codes.
                    </p>
                  </div>
                </div>
              ) : null}

              {cameraError && (
                <div className="bg-yellow-50 border-l-4 border-yellow-500 text-yellow-800 p-4 rounded mb-6">
                  <div className="flex gap-3 mb-2">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <p className="font-semibold">{cameraError}</p>
                  </div>
                </div>
              )}

              {scannedQRs.length > 0 && (
                <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
                  <div className="flex justify-between items-center mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">
                        Scanned Codes
                      </h2>
                      <p className="text-sm text-gray-600 mt-1">
                        {scannedQRs.length} QR code(s) scanned
                      </p>
                    </div>
                    <button
                      onClick={clearAll}
                      className="text-sm bg-red-100 hover:bg-red-200 text-red-700 px-4 py-2 rounded-lg transition font-semibold"
                    >
                      Clear All
                    </button>
                  </div>

                  <div className="space-y-3 max-h-96 overflow-y-auto mb-6">
                    {scannedQRs.map((qr) => (
                      <div
                        key={qr.id}
                        className={`flex items-center justify-between p-4 rounded-lg border-2 transition group ${
                          qr.validationStatus === "valid"
                            ? "bg-green-50 border-green-400"
                            : qr.validationStatus === "error"
                            ? "bg-red-50 border-red-400"
                            : "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-300"
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-1">
                            {validatingQRId === qr.code ? (
                              <Loader className="w-5 h-5 text-blue-600 animate-spin" />
                            ) : qr.validationStatus === "valid" ? (
                              <Check className="w-5 h-5 text-green-600" />
                            ) : (
                              <AlertCircle className="w-5 h-5 text-red-600" />
                            )}
                            <p className="font-mono font-bold text-gray-900 text-lg break-all">
                              {qr.code}
                            </p>
                          </div>
                          <p className="text-xs text-gray-600 ml-8">
                            {qr.timestamp}
                          </p>
                          {qr.validationStatus && (
                            <p
                              className={`text-xs ml-8 mt-1 font-semibold ${
                                qr.validationStatus === "valid"
                                  ? "text-green-600"
                                  : "text-red-600"
                              }`}
                            >
                              {qr.validationStatus === "valid"
                                ? "✓ Validated"
                                : "✗ Validation Failed"}
                            </p>
                          )}
                          {qr.validationError && (
                            <p className="text-xs text-red-600 ml-8 mt-1">
                              {qr.validationError}
                            </p>
                          )}
                          {validatingQRId === qr.code && (
                            <p className="text-xs text-blue-600 ml-8 mt-1 font-semibold">
                              Validating...
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => removeQR(qr.id)}
                          className="ml-2 p-2 text-red-600 hover:bg-red-100 rounded transition flex-shrink-0"
                          title="Delete"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={handleAccumulate}
                    disabled={
                      isAccumulating || scannedQRs.length === 0 || !customerId
                    }
                    className={`w-full py-4 rounded-lg font-bold text-lg transition flex items-center justify-center gap-2 ${
                      isAccumulating || scannedQRs.length === 0 || !customerId
                        ? "bg-gray-300 cursor-not-allowed text-gray-500"
                        : "bg-amber-500 hover:bg-amber-800 text-white shadow-lg"
                    }`}
                  >
                    {isAccumulating ? (
                      <>
                        <Loader className="w-6 h-6 animate-spin" />
                        Accumulating...
                      </>
                    ) : (
                      <>Accumulate ({scannedQRs.length})</>
                    )}
                  </button>
                </div>
              )}

              {scannedQRs.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500 text-lg">
                    No QR codes scanned yet
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        <style>{`
        @keyframes scan {
          0% { top: 0; }
          50% { top: 100%; }
          100% { top: 0; }
        }
      `}</style>

        {/* Use Modal Components */}
        <AccumulationResultModal
          showResultModal={showResultModal}
          accumulationResult={accumulationResult}
          onClose={() => setShowResultModal(false)}
          onScanMore={() => {
            setShowResultModal(false);
            setScannedQRs([]);
          }}
          onRedeemGift={handleRedeemGift}
        />

        <RedeemGiftModal
          showRedeemModal={showRedeemModal}
          onClose={() => setShowRedeemModal(false)}
          onSubmit={handleSubmitRedeem}
          isSubmitting={isSubmittingRedeem}
        />
      </div>
    </DashboardLayout>
  );
};

export default QRScanner;

